// What "proven" means, written down once, in numbers nothing else retypes.
//
// The app says "proven" in four places — the gate that lets a model strategy
// print, the Best bets sheet, the strategy explainers and the glossary — and
// until this file existed it meant a slightly different thing in each of them.
// A bar that is described four ways is a bar somebody will quietly lower.
//
// THE BAR IS FIXED BEFORE THE RESULT IS KNOWN, which is the whole of why it is
// here rather than inside whichever screen happens to be asking. A threshold
// chosen after looking at the answer is not a threshold.
//
// TWO CONDITIONS, AND BOTH HAVE TO HOLD, for one strategy in one sport:
//
//   (a) IN THE HARNESS. The walk-forward encompassing test says the model adds
//       information beyond the market's price: `t2 >= T_CRITICAL` (1.96) on
//       `n >= MIN_MATCHES` (100) scored matches. Both numbers are imported from
//       `encompassing.js`, which is where they are decided.
//
//   (b) ON THE PAPER. The strategy's own per-leg closing-line value, over at
//       least `PROOF_MIN_LEGS` legs that had a close, has a 95% interval whose
//       LOWER BOUND is above zero. Not the sign of the mean: a mean of +0.4
//       points over three hundred legs whose interval runs from −1.9 to +2.7 is
//       a strategy that has shown nothing, and calling that proven would be the
//       app making exactly the claim it tells the punter not to make.
//
// (a) is a backtest. It is necessary and it is not enough: a backtest is fitted
// on history that has already happened, on prices nobody had to get on at, and
// every honest one of them flatters itself somewhere. (b) is the same claim
// made forward, on the prices the app actually printed, and it is the one that
// costs something to be wrong about.
//
// WHAT HAPPENS IN BETWEEN. A strategy with (a) and not (b) still PRINTS — it
// has cleared the only bar the engine can check before a leg exists — but it
// says so: its eyebrow carries "on trial", and it is not eligible for Best
// bets. The sheet is the app's loudest claim and it does not get to lean on a
// backtest alone.
//
// CONTROLS ARE NEVER PROVEN, and that is not a slight. Classic and the racing
// all-up have no model: they measure no edge, every leg of them is worth minus
// the book's margin, and they exist to be the thing an idea has to beat. A
// control that could be promoted is not a control.
//
// Pure: no store, no clock, no DOM. The gate, the explainers and the glossary
// all read this.

import { MIN_MATCHES, T_CRITICAL, fitKey, fitProven } from './encompassing.js';

/** Re-exported so a caller never has to know which file a number lives in. */
export { MIN_MATCHES, T_CRITICAL };

/**
 * The fewest settled legs with a closing price before the forward half of the
 * bar can be read at all.
 *
 * Twice `EVIDENCE_MIN_LEGS` — the bar for PRINTING a CLV figure on a card —
 * because this is the bar for BELIEVING one, and for changing what the app
 * recommends off the back of it. At a 1% per-leg edge and typical leg variance
 * two hundred legs is roughly where a 95% interval stops straddling zero, which
 * is the number the condition is actually about.
 */
export const PROOF_MIN_LEGS = 200;

/** The four things a strategy can be, in the order they are climbed. */
export const PROOF_STATES = ['control', 'unproven', 'on-trial', 'proven'];

/** What each of them is called on a screen. `proven` prints nothing: it is the norm. */
export const PROOF_LABEL = {
  control: 'control',
  unproven: '',
  'on-trial': 'on trial',
  proven: '',
};

/** The words the eyebrow carries while the forward half is still being measured. */
export const ON_TRIAL = PROOF_LABEL['on-trial'];

/**
 * Does this strategy make a claim a bar could be applied to at all?
 *
 * Read off the document — `probabilitySource.model` — rather than off a list of
 * ids, so a fork of Classic is still a control and a fork of Elo Edge is still
 * something to measure. `none` is the book's own price devigged: there is no
 * opinion in it to be right or wrong about.
 */
export const isControl = (strategy) => {
  const model = strategy?.probabilitySource?.model;
  return !model || model === 'none';
};

/**
 * Condition (a): the harness half. True where the fit for this model and this
 * sport clears the t threshold on enough scored matches.
 *
 * `fitProven` is the same predicate `runStrategy` bets on; the sample size is
 * checked here as well as inside `encompassingTest` because this file is where
 * somebody comes to read what the bar IS, and a condition that is only true by
 * accident of another module's early return is not written down.
 */
export function harnessProven(fit) {
  if (!fitProven(fit)) return false;
  const n = Number(fit?.n);
  return Number.isFinite(n) && n >= MIN_MATCHES;
}

/**
 * Condition (b): the paper half. `agg` is one strategy's aggregate off the
 * trail — `{ legsWithClose, clvInterval }` as `insights.js` expands one — and
 * the answer is whether its interval has cleared zero over enough legs.
 */
export function trailProven(agg) {
  const legs = Number(agg?.legsWithClose) || 0;
  if (legs < PROOF_MIN_LEGS) return false;
  const lo = agg?.clvInterval?.lo;
  return Number.isFinite(lo) && lo > 0;
}

/**
 * Where one strategy stands in one sport: `'control'`, `'unproven'`,
 * `'on-trial'` or `'proven'`.
 *
 * `fits` is `${modelId}:${sport}` -> the encompassing fit (the same map
 * `runStrategy` reads as `ctx.modelFits`); `agg` is that strategy's row off the
 * paper trail. A market strategy — one whose "model" IS a market price — has no
 * fit to clear and starts at `on-trial`: there is nothing for the harness half
 * to say about it, so the forward half decides on its own.
 */
export function proofStateOf(strategy, sport, { fits = {}, agg = null } = {}) {
  if (isControl(strategy)) return 'control';
  const modelId = strategy?.probabilitySource?.model;
  const fit = fits?.[fitKey(modelId, sport)] ?? null;
  // A model that IS a market price (market-consensus, sharp-anchor) is never
  // handed a fit by anything, and `correctsMarket` is the engine's own test for
  // it. Rather than import that and split the answer two ways, the rule here is
  // the plain one: no fit filed means the harness has not spoken, and the
  // forward half is then the whole bar.
  if (fit && !harnessProven(fit)) return 'unproven';
  return trailProven(agg) ? 'proven' : 'on-trial';
}

/**
 * May this strategy be called proven in this sport?
 *
 * The one predicate the Best bets sheet asks. Everything else — what prints,
 * what carries "on trial" — is `proofStateOf`.
 */
export function isProven(strategy, sport, ctx = {}) {
  return proofStateOf(strategy, sport, ctx) === 'proven';
}

/**
 * The same question over a whole ticket: every sport on it has to clear the
 * bar, because a fit is about a sport and a two-code multi makes two claims.
 * A ticket with no sport on its legs has nothing to check and is not proven.
 */
export function ticketProven(strategy, sports, ctx = {}) {
  const list = [...new Set((sports ?? []).filter(Boolean))];
  if (!list.length) return false;
  return list.every((sport) => isProven(strategy, sport, ctx));
}

/**
 * THE BAR, IN THE WORDS THE GLOSSARY AND THE EXPLAINERS PRINT. Built out of the
 * constants rather than typed beside them, so a number that moves takes its own
 * description with it.
 */
export const PROOF_SENTENCE = `A strategy is proven in a sport once two things hold: in the harness, `
  + `its model adds information beyond the market's price (t of at least ${T_CRITICAL} over at least `
  + `${MIN_MATCHES} scored matches), and on the paper trail its own prices have beaten the close over at `
  + `least ${PROOF_MIN_LEGS} settled legs, with the whole 95% interval above zero. `
  + `Until the second one holds it still prints, marked "${ON_TRIAL}", and it is not a best bet. `
  + `The bar is fixed before the result is known.`;
